/**
 * Grocery List Sync Service
 *
 * Handles synchronization of grocery list data to/from Firestore.
 * Syncs: items (with checked status), custom items, generation metadata.
 */

import {
  doc,
  setDoc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { firestore, isFirebaseEnabled } from './firebaseConfig';
import { GroceryItem, WasteReductionMetrics } from '../types/groceryList';
import { resolveDataScope, buildScopedPath } from './householdPaths';

/**
 * Recursively remove all `undefined` values from an object.
 * Firestore rejects documents containing `undefined` field values.
 */
export function removeUndefined<T>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj
      .filter(item => item !== undefined)
      .map(item =>
        item !== null && typeof item === 'object' ? removeUndefined(item) : item
      ) as T;
  }
  if (obj !== null && typeof obj === 'object') {
    const clean: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value === undefined) continue;
      clean[key] = value !== null && typeof value === 'object'
        ? removeUndefined(value)
        : value;
    }
    return clean as T;
  }
  return obj;
}

/**
 * Firestore document structure for grocery list
 */
export interface FirestoreGroceryList {
  items: GroceryItem[];
  customItems: GroceryItem[];
  generatedDate: string | null;
  weekSnapshot: string;
  sourceWeekStart: string | null;
  /** Absent on documents written before #722. */
  coversUntil?: string | null;
  updatedAt: Timestamp | string;
  userId: string;
}

/**
 * Result of fetching grocery list from Firestore
 */
export interface GroceryListSyncResult {
  items: GroceryItem[];
  customItems: GroceryItem[];
  generatedDate: string | null;
  weekSnapshot: string;
  sourceWeekStart: string | null;
  /** Undefined when the document predates the field — keep the local value. */
  coversUntil?: string | null;
  updatedAt: string;
}

/**
 * Get reference to the active scope's grocery list document (household when
 * paired, else the user's own).
 */
const getGroceryListDocRef = (
  userId: string,
  householdId?: string | null
) => {
  if (!firestore) {
    throw new Error('Firestore not initialized');
  }
  const scope = resolveDataScope(userId, householdId);
  if (!scope) {
    throw new Error('Cannot resolve data scope: missing userId and householdId');
  }
  const [root, id, ...rest] = buildScopedPath(
    scope,
    'groceryList',
    'current'
  );
  return doc(firestore, root, id, ...rest);
};

/**
 * Sync grocery list to Firestore
 */
export const syncGroceryListToCloud = async (
  userId: string,
  items: GroceryItem[],
  customItems: GroceryItem[],
  generatedDate: string | null,
  weekSnapshot: string,
  sourceWeekStart: string | null,
  householdId?: string | null,
  coversUntil?: string | null
): Promise<void> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }

  const docRef = getGroceryListDocRef(userId, householdId);

  const data: FirestoreGroceryList = {
    items,
    customItems,
    generatedDate,
    weekSnapshot,
    sourceWeekStart,
    ...(coversUntil !== undefined ? { coversUntil } : {}),
    updatedAt: serverTimestamp() as Timestamp,
    userId,
  };

  await setDoc(docRef, removeUndefined(data), { merge: true });
  console.log('✅ Synced grocery list to cloud');
};

/**
 * Fetch grocery list from Firestore
 */
/**
 * Live-subscribe to the grocery list document under the active scope.
 * Fires immediately with the current value, then on every server-side
 * change (e.g. partner toggling an item).
 */
export const subscribeToGroceryList = (
  userId: string,
  householdId: string | null | undefined,
  onUpdate: (data: GroceryListSyncResult | null) => void,
  onError?: (error: Error) => void
): (() => void) => {
  if (!isFirebaseEnabled() || !firestore) {
    return () => {};
  }
  const ref = getGroceryListDocRef(userId, householdId);
  return onSnapshot(
    ref,
    (snapshot) => {
      if (!snapshot.exists()) {
        onUpdate(null);
        return;
      }
      const data = snapshot.data() as FirestoreGroceryList;
      onUpdate({
        items: data.items || [],
        customItems: data.customItems || [],
        generatedDate: data.generatedDate,
        weekSnapshot: data.weekSnapshot || '',
        sourceWeekStart: data.sourceWeekStart ?? null,
        coversUntil: data.coversUntil,
        updatedAt:
          typeof data.updatedAt === 'string'
            ? data.updatedAt
            : data.updatedAt?.toDate?.()?.toISOString() || '',
      });
    },
    (error) => {
      console.error('Grocery list subscription error:', error);
      onError?.(error);
    }
  );
};

export const fetchGroceryListFromCloud = async (
  userId: string,
  householdId?: string | null
): Promise<GroceryListSyncResult | null> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }

  const docRef = getGroceryListDocRef(userId, householdId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data() as FirestoreGroceryList;
  return {
    items: data.items || [],
    customItems: data.customItems || [],
    generatedDate: data.generatedDate,
    weekSnapshot: data.weekSnapshot || '',
    sourceWeekStart: data.sourceWeekStart ?? null,
    coversUntil: data.coversUntil,
    updatedAt: typeof data.updatedAt === 'string'
      ? data.updatedAt
      : data.updatedAt?.toDate?.()?.toISOString() || '',
  };
};

/**
 * Merge local and cloud grocery lists
 * Strategy:
 * - If cloud has newer generatedDate, use cloud items
 * - Merge checked status: if either is checked, keep checked
 * - Preserve local custom items not in cloud
 */
export const mergeGroceryLists = (
  local: {
    items: GroceryItem[];
    customItems: GroceryItem[];
    generatedDate: string | null;
    weekSnapshot: string;
    sourceWeekStart: string | null;
    coversUntil?: string | null;
  },
  cloud: GroceryListSyncResult | null
): {
  items: GroceryItem[];
  customItems: GroceryItem[];
  generatedDate: string | null;
  weekSnapshot: string;
  sourceWeekStart: string | null;
  coversUntil?: string | null;
} => {
  if (!cloud) {
    return local;
  }

  // If week snapshots match, merge checked status
  if (local.weekSnapshot === cloud.weekSnapshot) {
    // Create a map of cloud item checked status
    const cloudCheckedMap = new Map<string, boolean>();
    cloud.items.forEach(item => {
      cloudCheckedMap.set(item.id, item.isChecked);
    });

    // Merge: if either local or cloud is checked, keep it checked
    const mergedItems = local.items.map(item => {
      const cloudChecked = cloudCheckedMap.get(item.id);
      return {
        ...item,
        isChecked: item.isChecked || cloudChecked || false,
      };
    });

    // Merge custom items
    const localCustomIds = new Set(local.customItems.map(i => i.id));
    const mergedCustomItems = [
      ...local.customItems,
      ...cloud.customItems.filter(i => !localCustomIds.has(i.id)),
    ];

    return {
      items: mergedItems,
      customItems: mergedCustomItems,
      generatedDate: local.generatedDate,
      weekSnapshot: local.weekSnapshot,
      sourceWeekStart: local.sourceWeekStart ?? cloud.sourceWeekStart,
      coversUntil: local.coversUntil !== undefined ? local.coversUntil : cloud.coversUntil,
    };
  }

  // If cloud has a different/newer week, use cloud data
  const cloudDate = cloud.generatedDate ? new Date(cloud.generatedDate).getTime() : 0;
  const localDate = local.generatedDate ? new Date(local.generatedDate).getTime() : 0;

  if (cloudDate > localDate) {
    console.log('☁️ Using cloud grocery list (newer)');
    return {
      items: cloud.items,
      customItems: cloud.customItems,
      generatedDate: cloud.generatedDate,
      weekSnapshot: cloud.weekSnapshot,
      sourceWeekStart: cloud.sourceWeekStart,
      // The cloud list wins wholesale, so its range comes with it — the range
      // is what its items were built from.
      coversUntil: cloud.coversUntil !== undefined ? cloud.coversUntil : local.coversUntil,
    };
  }

  console.log('📝 Using local grocery list (newer or same)');
  return local;
};
